// ======================================================================
// File: frontend/src/modules/ppe/pages/procurement/indent/view/IndentDetailPage.js
// Module: PPE → Procurement → Indent
//
// Purpose:
// - Indent detail view page
// - Displays indent header, items, and workflow actions
// - Handles submit / approve lifecycle
//
// ======================================================================

import React, { useEffect, useState, useContext } from "react";
//import { useParams, useLocation } from "react-router-dom";
//import { useParams, useSearchParams } from "react-router-dom";
import {
  useParams,
  useLocation,
  useNavigate
} from "react-router-dom";

// ----------------------------------------------------------------------
// AUTH CONTEXT
// ----------------------------------------------------------------------
import { AuthContext } from "../../../../../../context/AuthContext";

// ----------------------------------------------------------------------
// SERVICES & ENUMS
// ----------------------------------------------------------------------
import indentService from "../services/indentService";
import INDENT_STATUS from "../enums/indentStatus";

// ----------------------------------------------------------------------
// VIEW COMPONENTS
// ----------------------------------------------------------------------
import IndentHeaderCard from "./IndentHeaderCard";
import IndentItemTable from "./IndentItemTable";
import IndentTimeline from "./IndentTimeline";

// ----------------------------------------------------------------------
// DRAWERS
// ----------------------------------------------------------------------
import IndentItemDrawer from "../form/IndentItemDrawer";
import IndentApprovalDrawer from "../form/IndentApprovalDrawer";

import IndentFormDrawer from "../form/IndentFormDrawer";

// ----------------------------------------------------------------------
// MODULE CSS
// ----------------------------------------------------------------------
import "./indent-ui.css";

// ----------------------------------------------------------------------
// LOCAL PERMISSION HELPER
// ----------------------------------------------------------------------
const hasPermission = (permissions, key) =>
  permissions?.[key]?.view === true;

export default function IndentDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

const editMode =
  new URLSearchParams(location.search)
    .get("edit") === "true";


  const { permissions } = useContext(AuthContext);

  const [indent, setIndent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [itemDrawerOpen, setItemDrawerOpen] =
    useState(false);
  
  const [headerDrawerOpen, setHeaderDrawerOpen] =
    useState(false);

  const [approveOpen, setApproveOpen] =
    useState(false);

  // ----------------------------------------------------------------------
  // LOAD INDENT
  // ----------------------------------------------------------------------
  const loadIndent = async () => {
    try {
      setLoading(true);
      const data =
        await indentService.getIndentById(id);
      setIndent(data);
    } catch (err) {
      console.error("Failed to load indent", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndent();
  }, [id]);

  // ----------------------------------------------------------------------
  // STATE HANDLING
  // ----------------------------------------------------------------------
  if (loading)
    return <div className="erp-page">Loading...</div>;

  if (!indent)
    return (
      <div className="erp-page">
        Indent not found
      </div>
    );

  // ----------------------------------------------------------------------
  // PERMISSIONS
  // ----------------------------------------------------------------------
  const canEdit =
  editMode &&
  hasPermission(
    permissions,
    "PPE_INDENT_EDIT"
  ) &&
  indent.status === INDENT_STATUS.DRAFT;


  const canApprove =
    hasPermission(
      permissions,
      "PPE_INDENT_APPROVE"
    ) &&
    indent.status === INDENT_STATUS.SUBMITTED;

  // ----------------------------------------------------------------------
  // UI
  // ----------------------------------------------------------------------
  return (
    <div className="erp-page">

      {/* ====================================================== */}
      {/* PAGE HEADER (GRADIENT) */}
      {/* ====================================================== */}
      <IndentHeaderCard
        indent={indent}
        variant="page"
      />

      {/* ====================================================== */}
      {/* SUMMARY CARD (WHITE DETAILS) */}
      {/* ====================================================== */}
      <IndentHeaderCard
        indent={indent}
        variant="card"
      />

      {/* ====================================================== */}
      {/* ACTION BAR */}
      {/* ====================================================== */}
      <div className="erp-page-actions">
        <button
            className="btn btn-light"
            onClick={() =>
              navigate("/ppe/procurement/indent")
            }
          >
            ← Back
          </button>
        {canEdit && (
          <button
            className="btn btn-secondary"
            onClick={() => setHeaderDrawerOpen(true)}
          >
            Edit Header
          </button>
        )}
        
        {canEdit && (
          <button
            className="btn btn-primary"
            onClick={() =>
              setItemDrawerOpen(true)
            }
          >
            + Add Item
          </button>
        )}

        {canEdit && (
          <button
            className="btn btn-warning"
            disabled={
              !indent.lines ||
              indent.lines.length === 0
            }
            onClick={async () => {

              try {

                await indentService.submitIndent(
                  indent.id
                );

                loadIndent();

              } catch (err) {

                alert(
                  err?.response?.data?.message ||
                  "Failed to submit indent"
                );
              }

            }}
          >
            Submit
          </button>
        )}

        
      </div>

      {/* ====================================================== */}
      {/* ITEMS */}
      {/* ====================================================== */}
      <IndentItemTable
        items={indent.lines || []}
        status={indent.status}
        canEdit={canEdit}
        onRefresh={loadIndent}
      />

      {/* ====================================================== */}
      {/* TIMELINE */}
      {/* ====================================================== */}
      <IndentTimeline
        history={indent.history}
      />

      {/* ====================================================== */}
      {/* DRAWERS */}
      {/* ====================================================== */}
      
      <IndentFormDrawer
        open={headerDrawerOpen}
        mode="edit"
        record={indent}
        onClose={() => setHeaderDrawerOpen(false)}
        onSaved={() => {
          setHeaderDrawerOpen(false);
          loadIndent();
        }}
      />
      <IndentItemDrawer
        open={itemDrawerOpen}
        indentId={indent.id}
        onClose={() =>
          setItemDrawerOpen(false)
        }
        onSaved={loadIndent}
      />

      <IndentApprovalDrawer
        open={approveOpen}
        indent={indent}
        onClose={() => setApproveOpen(false)}
        onApproved={loadIndent}
      />
    </div>
  );
}
